import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { emitSessionExpired } from "./contexts/SessionExpiredContext";
import { emitSessionActivity } from "./contexts/SessionWarningContext";

// ToDo: delete later after initial environment setup
// Fallback: capture userId from URL on any route if present (helps staging)
(() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const qpUserId = params.get("userId");
    if (qpUserId && !localStorage.getItem("userId")) {
      console.log("[AuthBootstrap] Captured userId from URL", { qpUserId });
      localStorage.setItem("userId", qpUserId);
      try {
        // Also set a cookie for backend fallback parsing
        document.cookie = `userId=${encodeURIComponent(qpUserId)}; Path=/; SameSite=Lax`;
        console.log("[AuthBootstrap] userId cookie set for backend fallback");
      } catch (e) {
        console.warn("[AuthBootstrap] Failed to set userId cookie", e);
      }
    }
  } catch (e) {
    console.warn("[AuthBootstrap] Failed to parse URL for userId", e);
  }
})();

// NOTE: Do not auto-fetch CSRF token here. The app does it during init
// and the /access page does it before authenticating. Duplicated fetches
// can race and produce mismatched header/cookie pairs in some browsers.

// Check if we're on localhost and use appropriate API endpoint
const localDev = window.location.hostname === "localhost";
const backendPort = import.meta.env.VITE_BACKEND_PORT || "4000";

// Extra debug: log environment and expected backend API
console.debug("[Frontend/Auth] Init ApolloClient", {
  location: window.location.href,
  hostname: window.location.hostname,
  localDev,
  api: localDev ? `http://localhost:${backendPort}/api` : "/api",
});

const httpLink = createHttpLink({
  uri: localDev ? `http://localhost:${backendPort}/api` : "/api",
  credentials: "include", // Important: send cookies with requests
  fetchOptions: {
    mode: "cors", // Ensure cross-origin requests
  },
});

// Auth link: prefer Magic Link session token; fallback to mock auth in dev
const authLink = setContext((_, { headers }) => {
  // Enhanced logging to diagnose missing header issues

  // Magic link session token, if present, becomes the Authorization header
  // Try localStorage first, fall back to sessionStorage
  let mlToken = localStorage.getItem("magicLinkSessionToken");
  let tokenSource = "localStorage";

  if (!mlToken) {
    mlToken = sessionStorage.getItem("magicLinkSessionToken");
    tokenSource = "sessionStorage";
  }

  if (mlToken) {
    const h = {
      ...headers,
      Authorization: `Bearer ${mlToken}`,
    } as Record<string, string>;
    console.debug(
      "[AuthLink] Using magic link session token for Authorization",
      {
        source: tokenSource,
        tokenLength: mlToken.length,
        tokenPrefix: mlToken.substring(0, 10),
        headerKeys: Object.keys(h),
        timestamp: new Date().toISOString(),
      },
    );
    return { headers: h };
  }

  // Log if token is missing - this helps diagnose storage issues
  console.warn("[AuthLink] No magic link session token found", {
    checkedLocalStorage: true,
    checkedSessionStorage: true,
    localStorageAvailable: typeof localStorage !== "undefined",
    sessionStorageAvailable: typeof sessionStorage !== "undefined",
    timestamp: new Date().toISOString(),
  });

  // Detect Cloudflare Access context (header not visible to browser, but we can log hints)
  console.debug("[Frontend/Auth] Preparing request context", {
    hasUserId: !!localStorage.getItem("userId"),
    hasLocalEmail: !!localStorage.getItem("local_email"),
    csrfInMeta: !!document.querySelector('meta[name="csrf-token"]'),
  });

  // Always attempt to include mock auth when a userId is present
  const userId = localStorage.getItem("userId");
  if (userId) {
    const h = {
      ...headers,
      "x-mock-auth": `user-${userId}`,
    } as Record<string, string>;
    console.log("[AuthLink] Injecting x-mock-auth from localStorage userId", {
      userId,
      headerKeys: Object.keys(h),
    });
    return { headers: h };
  }

  // Fallback to email-based mock auth (legacy)
  const email = localStorage.getItem("local_email");
  if (email) {
    const h = {
      ...headers,
      "x-mock-auth": email,
    } as Record<string, string>;
    console.log("[AuthLink] Injecting x-mock-auth from local_email", {
      email,
      headerKeys: Object.keys(h),
    });
    return { headers: h };
  }

  console.warn(
    "[AuthLink] No userId/local_email found; x-mock-auth will be missing",
  );
  return { headers };
});

// Check for the correct header name (case is important)
const CSRF_HEADER_NAME = "CSRF-Token";

// CSRF middleware - adds token for all GraphQL operations
const csrfLink = new ApolloLink((operation, forward) => {
  // First try to get token from meta tag (backwards compatibility)
  const csrfMeta = document.querySelector('meta[name="csrf-token"]');
  let csrfToken = csrfMeta ? csrfMeta.getAttribute("content") : null;

  // If not available, try localStorage (from fetchCsrfToken)
  if (!csrfToken) {
    csrfToken = localStorage.getItem("csrfToken");
  }

  if (csrfToken) {
    // Get the current context
    const context = operation.getContext();

    console.log(
      `[Frontend/Auth] Adding CSRF token to GraphQL operation: ${operation.operationName}`,
      {
        token: `${csrfToken.substring(0, 5)}...`,
        tokenLength: csrfToken.length,
        headers: Object.keys(context.headers || {}).join(", "),
        operation: operation.operationName,
      },
    );

    // Add CSRF token to headers for all operations
    operation.setContext({
      ...context,
      headers: {
        ...context.headers,
        [CSRF_HEADER_NAME]: csrfToken,
        // Also include as X-CSRF-Token for wider compatibility
        "X-CSRF-Token": csrfToken,
      },
    });
  } else {
    console.warn(
      "[Frontend/Auth] No CSRF token available for GraphQL operation:",
      operation.operationName,
    );
  }

  return forward(operation).map((response) => {
    console.log(`GraphQL response for ${operation.operationName}:`, {
      success: !response.errors,
      hasErrors: !!response.errors,
      errorCount: response.errors?.length || 0,
    });
    return response;
  });
});

// Set up mock auth for local development (using email-based auth)
if (
  localDev &&
  !localStorage.getItem("local_email") &&
  !localStorage.getItem("userId")
) {
  // For backwards compatibility, maintain the email-based auth option
  const email = window.prompt(
    "Please enter your email for local development (or cancel to use role-based auth):",
  );
  if (email) {
    localStorage.setItem("local_email", email);
  }
}

// Create a cache with proper type policies
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        allFiles: {
          merge: false, // Don't merge results, always use the latest
        },
        filesByClient: {
          merge: false, // Don't merge results, always use the latest
          // Force network-only fetches for filesByClient query
          read() {
            return undefined; // Return undefined to force a fetch every time
          },
        },
        admin: {
          merge: true,
        },
      },
    },
    AdminQuery: {
      fields: {
        files: {
          merge: false, // Don't merge results, always use the latest
        },
      },
    },
  },
});

// Session expiry detection link — catches auth errors globally and triggers
// the session-expired modal instead of letting individual components show
// generic error messages.
//
// Two error surfaces must be handled:
// 1. networkError (HTTP 401/403): Auth middleware rejected the request before
//    it reached Apollo Server. All 401s indicate session expiry. For 403s,
//    only CSRF_ERROR indicates session expiry — other 403s (USER_INACTIVE,
//    OPERATION_NOT_ALLOWED) are permission errors.
// 2. graphQLErrors (HTTP 200): The request reached Apollo Server but the
//    resolver threw UNAUTHENTICATED. This can happen if middleware passed
//    but the resolver-level auth check failed.
//
// Public auth operations excluded from session expiry detection.
// These are fresh auth attempts with no existing session — a 401/UNAUTHENTICATED
// response means the token/email is invalid, not that the user's session expired.
// Showing "Your session has expired" would be misleading and would hide each
// page's own error UI (e.g., the "Request New Link" button on the auth page).
const PUBLIC_AUTH_OPERATIONS = new Set([
  "ExchangeMagicLinkToken",
  "RequestMagicLink",
]);

const sessionErrorLink = onError(
  ({ networkError, graphQLErrors, operation }) => {
    // Skip session expiry detection for public auth mutations.
    if (PUBLIC_AUTH_OPERATIONS.has(operation.operationName)) {
      return;
    }

    // Check HTTP-level auth failures (middleware rejections)
    if (networkError && "statusCode" in networkError) {
      const status = (networkError as { statusCode: number }).statusCode;
      if (status === 401) {
        // All 401s are auth/token failures — trigger session expired
        console.warn("[SessionErrorLink] Auth error detected, status:", status);
        emitSessionExpired();
        return;
      }
      if (status === 403) {
        // Only CSRF errors indicate session expiry; other 403s are permission errors
        // (USER_INACTIVE, OPERATION_NOT_ALLOWED, FORBIDDEN)
        const body = (networkError as unknown as { result?: { code?: string } })
          .result;
        if (body?.code === "CSRF_ERROR") {
          console.warn("[SessionErrorLink] CSRF error detected");
          emitSessionExpired();
          return;
        }
      }
    }

    // Check GraphQL-level auth failures (resolver rejections with HTTP 200)
    if (graphQLErrors?.some((e) => e.extensions?.code === "UNAUTHENTICATED")) {
      console.warn("[SessionErrorLink] GraphQL UNAUTHENTICATED error detected");
      emitSessionExpired();
    }
  },
);

// Track activity for session warning timer reset (SSO users).
// Emits an event on every successful Apollo response so SessionWarningContext
// can reset the SSO idle timer. Placed after sessionErrorLink so error
// detection happens first — we only track activity for non-error responses.
const sessionActivityLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    if (!response.errors || response.errors.length === 0) {
      emitSessionActivity();
    }
    return response;
  });
});

// Create link chain with CSRF middleware and auth (enable auth header if userId is present in any env)
const linkChain = ApolloLink.from([
  sessionErrorLink, // Detect session expiry first
  sessionActivityLink, // Track activity for warning timer reset (SSO users)
  // Inspector link to log all actual request headers
  new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      // Log request headers for debugging when errors occur
      if (response.errors) {
        console.error(
          "[InspectorLink] Request failed. Headers sent:",
          operation.getContext().headers,
        );
      }
      return response;
    });
  }),
  csrfLink, // Always apply CSRF first
  // Always attempt to inject x-mock-auth when userId or local_email exists.
  // In non-mock environments, extra header is ignored server-side.
  authLink,
  httpLink, // Base HTTP link
]);

export const client = new ApolloClient({
  link: linkChain,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
    query: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
    mutate: {
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    },
  },
});
